import 'unfetch/polyfill';
import set from 'lodash/set';
import get from 'lodash/get';
import unset from 'lodash/unset';
import { randomize } from '../utils/StringUtils';
import { isNonEmptyArray } from '../utils/ArrayUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4042/api';

/**
 * Convert a `File` object returned by the upload input into
 * a base 64 string. That's easier to use on FakeRest, used on
 * the ng-admin example. But that's probably not the most optimized
 * way to do in a production database.
 */
export const uploadToS3 = (image, title, resource) => {
  const body = new FormData();
  body.append('file', image.file);
  body.append('key', image.key);
  body.append('title', title);
  body.append('meta', JSON.stringify(image.meta || {}));
  body.append('resource', resource);
  return fetch(`${API_BASE_URL}/utils/upload-image`, {
    method: 'POST',
    credentials: 'include',
    body,
  }).then((res) => res.json());
};

/**
 * Finds all sub documents in obj
 * which satisfies the predicate and
 * returns it with the corresponding path.
 * Example result: {
 *  obj: { a: 1 },
 *  path: 'c.0.b.0'
 * }
 */
const findSubDocuments = (obj, predicate, path = '') => {
  if (predicate(obj)) {
    return [{ obj, path }];
  } else if (typeof obj === 'object' && obj !== null) {
    let findings = [];
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i += 1) {
      const nextPath = path ? `${path}.${keys[i]}` : keys[i];
      findings = [...findings, ...findSubDocuments(obj[keys[i]], predicate, nextPath)];
    }
    return findings;
  }
  return [];
};

/**
 * Helper to append to an array
 * deep within a target object
 */
const append = (target, path, obj) => {
  const current = get(target, path);
  if (current) {
    if (Array.isArray(current)) {
      set(target, path, [...current, obj]);
    } else {
      set(target, path, [current, obj]);
    }
  } else {
    set(target, path, [obj]);
  }
};

/**
 * Helper to assign values to attributes in the form which
 * are marked as derived. Mutates the "data" object
 */
const assignDerivedAttributes = (data) => {
  const derived = findSubDocuments(data, (o) => o && o.derivedFrom);
  derived.forEach(({ obj: { derivedFrom }, path }) => {
    const content = get(data, derivedFrom);
    set(data, path, content);
  });
};

// Middleware to upload images in quiz data to S3
const uploadQuizImages = (requestHandler, type, resource, params) => {
  const images = findSubDocuments(params.data, (o) => o && o.rawFile);
  const data = { ...params.data };
  if (images.length > 0) {
    // Upload all the submitted and parsed images to S3
    const promises = images
      .map(({ obj: { rawFile, source, onlyUrl, asArray }, path }) => ({
        key: rawFile.name || 'no-name',
        file: rawFile,
        meta: {
          onlyUrl: !!(onlyUrl),
          asArray: !!(asArray),
          path: source || path,
        },
      }))
      .map(({ file, meta }) => uploadToS3({ key: file.name, file, meta }, randomize.separatedNumbers(), resource));
    return Promise.all(promises)
    // Pre-parse meta data
      .then((uploaded) => uploaded.map((image) => {
        let meta;
        try {
          meta = JSON.parse(image.meta);
        } catch (err) {
          meta = {};
        }
        return {
          ...image,
          meta,
        };
      }))
      .then((uploaded) => {
        // Make sure that the "source" keys are wiped with original File objects
        uploaded.forEach(({ meta: { asArray, path } }) => {
          if (asArray) {
            const values = get(data, path);
            set(data, path, values.filter((v) => v && !v.rawFile));
          } else {
            unset(data, path);
          }
        });
        // Inject the image data on the correct path
        uploaded.forEach((image) => {
          const { imageUrl, key, meta } = image;
          let imageData = { src: imageUrl, title: key };
          if (meta.onlyUrl) {
            imageData = imageUrl;
          }
          if (meta.asArray) {
            append(data, meta.path, imageData);
          } else {
            set(data, meta.path, imageData);
          }
        });
        // Manage attributes derived from other attributes after upload
        assignDerivedAttributes(data);
        // Assemble the data without File objects
        const payload = {
          ...params,
          data: {
            ...data,
          },
        };
        return requestHandler(type, resource, payload);
      });
  }
  // Set derived attributes not yest set
  assignDerivedAttributes(data);
  return requestHandler(type, resource, params);
};

/**
* For posts update only, convert uploaded image in base 64 and attach it to
* the `picture` sent property, with `src` and `title` attributes.
*/
const addUploadCapabilities = (requestHandler) => (type, resource, params) => {
  const whitelistedResource = [
    'products', 'books', 'provisional-products',
    'markets', 'categories', 'web-promos', 'quizes',
    'web-pages', 'web-blocks', 'news',
  ];
  const whitelistedMethods = ['UPDATE', 'CREATE'];
  const allowedResource = whitelistedResource.indexOf(resource) > -1;
  const allowedMethod = whitelistedMethods.indexOf(type) > -1;
  if (allowedResource && allowedMethod) {
    const images = [];

    if (resource === 'quizes') {
      return uploadQuizImages(requestHandler, type, resource, params);
    }

    // Find all the keys in the params.data that are arrays
    const arrayKeys = Object.keys(params.data).filter((key) => (
      isNonEmptyArray(params.data[key])
    ));

    // Go through the keys that are arrays
    arrayKeys.forEach((key) => (
      params.data[key].forEach((deepArray, index) => {
        // if the key data includes rawFile
        if (deepArray && deepArray.rawFile) {
          // we want to push that key and the rawFile into our images array
          images.push({ key, file: deepArray.rawFile });
        } else {
          // if there is no rawFile it means it could be one layer deeper
          // so we go through and find any keys in the key data that are arrays
          const deepArrayKeys = Object.keys(deepArray || {}).filter((dKey) => (
            isNonEmptyArray(params.data[key][index][dKey])
          ));
          // iterate the deep keys that are arrays
          deepArrayKeys.forEach((dKey) => (
            // if any of these deep values are rawFiles, once more add them to our images array, with index to reference later
            params.data[key][index][dKey].forEach((ary) => ary.rawFile && images.push({ key: `${key}.${dKey}`, index, file: ary.rawFile }))
          ));
        }
      })
    ));

    if (images.length > 0) {
      // const image = images[0].file;
      // const reader = new FileReader();
      // reader.readAsDataURL(image);
      // const img = new Image();
      // img.src = reader.result;
      // img.onload(() => {
      //   debugger;
      //   if (this.height < 2000 || this.width < 2000) {
      //     throw new Error('Image dimensions too small');
      //   }
      // });
      // Upload all the submitted and parsed images to S3
      return Promise.all(images.map((image) => uploadToS3({ ...image }, randomize.separatedNumbers(), resource)))
        .then((uploadedImages) => {
          const imagePartial = {};

          // Rebuild the structure of the object to put into payload
          uploadedImages.forEach((image) => {
            const [key, subkey] = image.key.split('.');
            // if we have key AND subkey it means it's a deep object file
            if (key && subkey && image.index !== undefined) {
              // initialize the structure with data
              if (!imagePartial[key]) {
                imagePartial[key] = [...params.data[key]];
              }
              // if the current iteratee's image.index already exists we overwrite it with the same data + image data
              if (imagePartial[key][image.index]) {
                imagePartial[key][image.index] = {
                  ...params.data[key][image.index],
                  [subkey]: { src: image.imageUrl, title: subkey },
                };
              } else {
                // and if it doest exist, we push it into the partial
                imagePartial[key].push({
                  ...params.data[key][image.index],
                  [subkey]: { src: image.imageUrl, title: subkey },
                });
              }
            } else if (key && !subkey) {
              imagePartial[key] = { src: image.imageUrl, title: key };
            }
          });

          const payload = {
            ...params,
            data: {
              ...params.data,
              ...imagePartial,
            },
          };

          return requestHandler(type, resource, payload);
        });
    }
  }

  return requestHandler(type, resource, params);
};

export default addUploadCapabilities;
